import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

import { GuidebookUpsellConfig } from 'src/models/guidebook-upsell-config.model';

export interface GuidebookConfig {
  id: number;
  property_id: number;
  property_name: string;
  property_address: string;
  guidebook_base_url: string;
  contact_collection_enabled: boolean;
  upsells_configured: boolean;
  upsells_count: number;
  identity_verification_enabled: boolean;
  welcome_message: string;
  check_in_instructions: string;
  check_out_instructions: string;
  wifi_network: string;
  wifi_password: string;
  house_rules: string;
  emergency_contact: string;
  created_at: string;
  updated_at: string;
}

@Injectable({
  providedIn: 'root'
})
export class GuidebookService {

  private http = inject(HttpClient);

  async getGuidebookConfigs(): Promise<GuidebookConfig[]> {
    return firstValueFrom(
      this.http.get<GuidebookConfig[]>(`${environment.api_url}api/v1/customer/guidebook-configs`)
    );
  }

  async getGuidebookConfig(id: number): Promise<GuidebookConfig> {
    return firstValueFrom(
      this.http.get<GuidebookConfig>(`${environment.api_url}api/v1/customer/guidebook-configs/${id}`)
    );
  }

  async updateGuidebookConfig(id: number, config: Partial<GuidebookConfig>): Promise<GuidebookConfig> {
    return firstValueFrom(
      this.http.put<GuidebookConfig>(`${environment.api_url}api/v1/customer/guidebook-configs/${id}`, config)
    );
  }

  async createGuidebookConfig(config: Partial<GuidebookConfig>): Promise<GuidebookConfig> {
    return firstValueFrom(
      this.http.post<GuidebookConfig>(`${environment.api_url}api/v1/customer/guidebook-configs`, config)
    );
  }

  async deleteGuidebookConfig(id: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${environment.api_url}api/v1/customer/guidebook-configs/${id}`)
    );
  }

  async getUpsellConfigs(guidebookConfigId: number): Promise<GuidebookUpsellConfig[]> {
    return firstValueFrom(
      this.http.get<GuidebookUpsellConfig[]>(
        `${environment.api_url}api/v1/customer/guidebook-configs/${guidebookConfigId}/upsell-configs`
      )
    );
  }

  async createUpsellConfig(
    guidebookConfigId: number,
    config: Partial<GuidebookUpsellConfig>
  ): Promise<GuidebookUpsellConfig> {
    return firstValueFrom(
      this.http.post<GuidebookUpsellConfig>(
        `${environment.api_url}api/v1/customer/guidebook-configs/${guidebookConfigId}/upsell-configs`,
        config
      )
    );
  }

  async updateUpsellConfig(
    guidebookConfigId: number,
    upsellConfigId: number,
    config: Partial<GuidebookUpsellConfig>
  ): Promise<GuidebookUpsellConfig> {
    return firstValueFrom(
      this.http.put<GuidebookUpsellConfig>(
        `${environment.api_url}api/v1/customer/guidebook-configs/${guidebookConfigId}/upsell-configs/${upsellConfigId}`,
        config
      )
    );
  }

  async deleteUpsellConfig(guidebookConfigId: number, upsellConfigId: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.api_url}api/v1/customer/guidebook-configs/${guidebookConfigId}/upsell-configs/${upsellConfigId}`
      )
    );
  }
}
